import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarCheck, Sparkles } from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { useAudit } from "@/components/landing/AuditContext";

/**
 * Sticky bottom CTA bar — mobile only.
 * Three clear actions:
 *   - "$297" pill → /build (Build Your OS page)
 *   - "Free audit" → AuditModal popup
 *   - "Book a Demo" → DemoForm popup
 * Hidden on /access (admin) and after the user scrolls past ~65% of the first viewport.
 */
export const MobileStickyCTA = () => {
  const openDemo = useDemo();
  const openAudit = useAudit();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const threshold = window.innerHeight * 0.65;
      setVisible(window.scrollY > threshold);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Don't render on admin
  if (pathname.startsWith("/access")) return null;

  const goToAudit = () => openAudit();

  return (
    <div
      data-testid="mobile-sticky-cta"
      aria-hidden={!visible}
      className={`fixed inset-x-3 bottom-3 z-40 sm:hidden transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
      }`}
    >
      <div className="flex items-stretch gap-2 rounded-2xl border border-violet-500/30 bg-card/95 p-1.5 shadow-2xl shadow-violet-600/20 backdrop-blur">
        <button
          type="button"
          data-testid="mobile-sticky-price"
          onClick={() => navigate("/build")}
          className="flex flex-none flex-col items-center justify-center rounded-xl bg-secondary/70 px-2.5 active:scale-[0.98] transition-transform"
          aria-label="Build Your OS — starts at $297"
        >
          <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">From</span>
          <span className="font-display text-[13px] font-black leading-none tracking-tight text-foreground">$297</span>
          <span className="text-[8.5px] font-semibold tracking-tight text-muted-foreground">/mo</span>
        </button>
        <button
          type="button"
          data-testid="mobile-sticky-audit"
          onClick={goToAudit}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-secondary px-2 py-2.5 text-[12px] font-semibold text-foreground active:scale-[0.98]"
        >
          <Sparkles className="h-3.5 w-3.5 text-violet-500" />
          Free audit
        </button>
        <button
          type="button"
          data-testid="mobile-sticky-book"
          onClick={openDemo}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-2 py-2.5 text-[12px] font-bold text-white shadow-lg shadow-violet-600/25 active:scale-[0.98]"
        >
          <CalendarCheck className="h-3.5 w-3.5" />
          Book a Demo
        </button>
      </div>
    </div>
  );
};
